import {
  COOKIES_ANALYTICS_CONSENT_KEY,
  COOKIES_CONSENT_CHANGE_EVENT,
  COOKIES_CONSENT_VALUE,
  type CookiesConsentRecord,
  type CookiesConsentValue,
} from '@/constants/cookies';

/**
 * Stable snapshot cache for `useSyncExternalStore`.
 * `getSnapshot` must return the same reference when the stored value is unchanged
 * (React compares with `Object.is`). Re-parsing JSON on every read would allocate a
 * new object each time and trigger an infinite re-render loop.
 *
 * The cache is the source of truth after the first hydrate or write. Cross-tab
 * updates call `syncAnalyticsConsentCacheFromStorage` from the `storage` listener.
 */
let cachedRaw: string | null | undefined;
let cachedRecord: CookiesConsentRecord | null = null;

function isConsentValue(value: unknown): value is CookiesConsentValue {
  return (
    value === COOKIES_CONSENT_VALUE.granted || value === COOKIES_CONSENT_VALUE.denied
  );
}

function parseConsentRecord(stored: string | null): CookiesConsentRecord | null {
  if (stored == null) return null;

  // Legacy plain-string storage — state only, no id/timestamp.
  if (isConsentValue(stored)) {
    return { consent: stored, id: null, decidedAt: null };
  }

  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      parsed != null &&
      typeof parsed === 'object' &&
      isConsentValue((parsed as { consent?: unknown }).consent)
    ) {
      const record = parsed as {
        consent: CookiesConsentValue;
        id?: unknown;
        decidedAt?: unknown;
      };
      return {
        consent: record.consent,
        id: typeof record.id === 'string' && record.id.length > 0 ? record.id : null,
        decidedAt:
          typeof record.decidedAt === 'string' && record.decidedAt.length > 0
            ? record.decidedAt
            : null,
      };
    }
  } catch {
    // Not JSON — treat as unknown/corrupt.
  }

  return null;
}

function setConsentCache(raw: string | null, record: CookiesConsentRecord | null): void {
  cachedRaw = raw;
  cachedRecord = record;
}

/**
 * Re-read localStorage into the snapshot cache.
 * Call from `storage` event handlers so other tabs can update consent.
 */
export function syncAnalyticsConsentCacheFromStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    const stored = window.localStorage.getItem(COOKIES_ANALYTICS_CONSENT_KEY);
    if (stored === cachedRaw) return;
    setConsentCache(stored, parseConsentRecord(stored));
  } catch {
    // Keep existing cache if storage is unavailable.
  }
}

/** Analytics gating — consent choice only (legacy or JSON record). */
export function readAnalyticsConsent(): CookiesConsentValue | null {
  return readAnalyticsConsentRecord()?.consent ?? null;
}

/** Full record for Cookie Settings; `id` / `decidedAt` null for legacy entries. */
export function readAnalyticsConsentRecord(): CookiesConsentRecord | null {
  if (typeof window === 'undefined') return null;

  // After hydrate/write, return the stable cached reference (required by useSyncExternalStore).
  if (cachedRaw !== undefined) {
    return cachedRecord;
  }

  try {
    const stored = window.localStorage.getItem(COOKIES_ANALYTICS_CONSENT_KEY);
    const record = parseConsentRecord(stored);
    setConsentCache(stored, record);
    return record;
  } catch {
    setConsentCache(null, null);
    return null;
  }
}

export function writeAnalyticsConsent(value: CookiesConsentValue): void {
  if (typeof window === 'undefined') return;

  const record: CookiesConsentRecord = {
    consent: value,
    id: crypto.randomUUID(),
    decidedAt: new Date().toISOString(),
  };
  const raw = JSON.stringify(record);

  // Update cache before notifying listeners so `getSnapshot` returns a stable
  // reference even when localStorage setItem fails (private mode / quota).
  setConsentCache(raw, record);

  try {
    window.localStorage.setItem(COOKIES_ANALYTICS_CONSENT_KEY, raw);
  } catch {
    // Private mode / quota — still notify listeners so UI can update.
  }

  window.dispatchEvent(
    new CustomEvent(COOKIES_CONSENT_CHANGE_EVENT, {
      detail: { consent: value, id: record.id, decidedAt: record.decidedAt },
    }),
  );
}
